import { lazy, type FC } from 'react';
import { useParams } from 'react-router';
import { useAuction } from 'src/apis/transport-auction/use-auction';
import { useBalancingGroups } from 'src/apis/transport-auction/use-balancing-groups';
import { useBiddingActivity } from 'src/apis/transport-auction/use-bidding-activity';
import { usePortfolioCodes } from 'src/apis/transport-auction/use-portfolio-codes';
import {
  useTransportAuctionServiceRoot,
  type OptionalTransportAuctionServiceRootRequest,
} from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useUpgradeableDealIds } from 'src/apis/transport-auction/use-upgradeable-deal-ids';
import {
  PageSpinner,
  SpinnerContainer,
} from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';

const Page = lazy(
  () => import('src/pages/transport/auctions/tiles/details/page')
);

export const BiddingActivity: FC = () => {
  const transportAuctionServiceRoot = useTransportAuctionServiceRoot();

  if (!transportAuctionServiceRoot.response) return <PageSpinner />;

  return <LoadData transportAuctionServiceRoot={transportAuctionServiceRoot} />;
};

const LoadData: FC<{
  transportAuctionServiceRoot: Successful<OptionalTransportAuctionServiceRootRequest>;
}> = ({ transportAuctionServiceRoot }) => {
  const { tileId, auctionId, biddingActivityId } = useParams<{
    tileId: string;
    auctionId: string;
    biddingActivityId: string;
  }>();

  const auction = useAuction({
    root: transportAuctionServiceRoot,
    tileId,
    auctionId,
  });

  const biddingActivity = useBiddingActivity({
    root: transportAuctionServiceRoot,
    auctionId,
    biddingActivityId,
  });

  const entryBalancingGroups = useBalancingGroups(
    auction.response?.data.bidding?.entry?._links?.assignedBalancingGroups
  );
  const exitBalancingGroups = useBalancingGroups(
    auction.response?.data.bidding?.exit?._links?.assignedBalancingGroups
  );
  const entryPortfolioCodes = usePortfolioCodes(
    auction.response?.data.bidding?.entry?._links?.assignedPortfolioCodes
  );
  const exitPortfolioCodes = usePortfolioCodes(
    auction.response?.data.bidding?.exit?._links?.assignedPortfolioCodes
  );
  const entryUpgradeableDealIds = useUpgradeableDealIds(
    auction.response?.data.bidding?.entry?._links?.upgradeableDealIds
  );
  const exitUpgradeableDealIds = useUpgradeableDealIds(
    auction.response?.data.bidding?.exit?._links?.upgradeableDealIds
  );
  const bothUpgradeableDealIds = useUpgradeableDealIds(
    auction.response?.data.bidding?._links?.upgradeableBundledDealIds
  );

  if (
    !auction.response ||
    !biddingActivity.response ||
    // Edit prefill must wait for select options
    entryBalancingGroups?.response === null ||
    exitBalancingGroups?.response === null ||
    entryPortfolioCodes?.response === null ||
    exitPortfolioCodes?.response === null ||
    entryUpgradeableDealIds?.response === null ||
    exitUpgradeableDealIds?.response === null ||
    bothUpgradeableDealIds?.response === null
  )
    return <PageSpinner />;

  return (
    <SpinnerContainer pending={auction.pending || biddingActivity.pending}>
      <Page
        auction={auction}
        entryBalancingGroups={entryBalancingGroups}
        exitBalancingGroups={exitBalancingGroups}
        entryPortfolioCodes={entryPortfolioCodes}
        exitPortfolioCodes={exitPortfolioCodes}
        entryUpgradeableDealIds={entryUpgradeableDealIds}
        exitUpgradeableDealIds={exitUpgradeableDealIds}
        bothUpgradeableDealIds={bothUpgradeableDealIds}
        biddingActivity={biddingActivity}
      />
    </SpinnerContainer>
  );
};
